import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { spriteUrl } from "./gameUtils";

// Shared visual kit for City Helpers: the sprite loader, sparkle bursts,
// floating hearts and a falling-rain layer. All presentational.

/** Draws a City Helpers PNG sprite, scaled to fit its box. */
export const Sprite = ({ name, alt = "", className = "", style }) => (
  <img
    src={spriteUrl(name)}
    alt={alt}
    aria-hidden={alt ? undefined : true}
    draggable="false"
    className={`ch-sprite ${className}`}
    style={style}
  />
);

// Line-art icons (no emoji) so controls stay crisp and on-brand in any theme.

/** Speaker with sound waves — the "read aloud" control. */
export const SpeakerIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5 6 9H3v6h3l5 4V5z" fill="currentColor" stroke="none" />
    <path d="M15.5 8.8a4.5 4.5 0 0 1 0 6.4" />
    <path d="M18.4 6a8 8 0 0 1 0 12" />
  </svg>
);

/** Muted speaker — the "sound off" state. */
export const MuteIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5 6 9H3v6h3l5 4V5z" fill="currentColor" stroke="none" />
    <line x1="16" y1="9.5" x2="21" y2="14.5" />
    <line x1="21" y1="9.5" x2="16" y2="14.5" />
  </svg>
);

/** Back chevron for the in-activity return control. */
export const BackIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 5 8 12l7 7" />
  </svg>
);

/** A simple check — used for completed states. */
export const CheckIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/** A filled heart shape for the drifting-hearts effect. */
export const HeartIcon = ({ size = 22, color = "#ff7a8a" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true" style={{ display: "block" }}>
    <path d="M12 20.7s-7.4-4.7-9.9-9C.8 9 1.7 5.7 4.8 4.7 6.9 4 9 5 10.2 6.7L12 9.1l1.8-2.4C15 5 17.1 4 19.2 4.7c3.1 1 4 4.3 2.7 7-2.5 4.3-9.9 9-9.9 9z" />
  </svg>
);

/** A five-point star — the burst/pip glyph used across the juice kit. */
export const StarShape = ({ size = 12, color = "#ffd45c" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true" style={{ display: "block" }}>
    <path d="M12 1.8 14.6 9l7.2.3-5.7 4.6 2 7-6.1-4.1L5.9 20.9l2-7L2.2 9.3 9.4 9z" />
  </svg>
);

const SPARK_TINTS = ["#ffd45c", "#ff8f6b", "#7bd6a0", "#8ca6ff", "#ffffff"];
const SPARKS = Array.from({ length: 12 }, (_, i) => {
  const angle = (i / 12) * Math.PI * 2;
  return {
    id: i,
    dx: Math.cos(angle),
    dy: Math.sin(angle),
    delay: (i % 4) * 0.025,
    tint: SPARK_TINTS[i % SPARK_TINTS.length],
    star: i % 3 === 0,
    size: 9 + ((i * 5) % 8),
  };
});

/**
 * A quick radial burst of sparkles — a mix of tinted motes and spinning stars.
 * Re-fires whenever `trigger` changes; place it absolutely and set left/top to
 * the burst point.
 */
export const SparkleBurst = ({ trigger, reach = 70, style }) => (
  <div className="ch-burst" style={style} aria-hidden="true">
    <AnimatePresence>
      {trigger > 0 && (
        <React.Fragment key={trigger}>
          {SPARKS.map((s) => (
            <motion.span
              key={s.id}
              className="ch-spark"
              style={
                s.star
                  ? { background: "none", width: s.size + 6, height: s.size + 6, margin: -(s.size + 6) / 2 }
                  : {
                      background: `radial-gradient(circle, #fff 0 30%, ${s.tint} 70%)`,
                      width: s.size,
                      height: s.size,
                      margin: -s.size / 2,
                    }
              }
              initial={{ x: 0, y: 0, scale: 0.2, opacity: 0, rotate: 0 }}
              animate={{ x: s.dx * reach, y: s.dy * reach, scale: 1, opacity: 0, rotate: s.star ? 140 : 0 }}
              transition={{ duration: 0.75, delay: s.delay, ease: "easeOut" }}
            >
              {s.star ? <StarShape size={s.size + 6} color={s.tint} /> : null}
            </motion.span>
          ))}
          <motion.span
            className="ch-spark-flash"
            initial={{ scale: 0.2, opacity: 0.85 }}
            animate={{ scale: 1.6, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </React.Fragment>
      )}
    </AnimatePresence>
  </div>
);

const HEARTS = [0, 1, 2];

/** Little hearts drifting up — used over a helped spot / a kind choice. */
export const FloatingHearts = ({ trigger = 1, style }) => (
  <div className="ch-hearts" style={style} aria-hidden="true">
    <AnimatePresence>
      {trigger > 0 &&
        HEARTS.map((i) => (
          <motion.span
            key={`${trigger}-${i}`}
            className="ch-heart"
            initial={{ y: 10, x: (i - 1) * 18, scale: 0.4, opacity: 0 }}
            animate={{ y: -70, scale: 1, opacity: [0, 1, 1, 0] }}
            transition={{ duration: 1.6, delay: i * 0.18, ease: "easeOut" }}
          >
            <HeartIcon />
          </motion.span>
        ))}
    </AnimatePresence>
  </div>
);

const RAINDROPS = Array.from({ length: 34 }, (_, i) => ({
  id: i,
  left: `${(i * 37) % 100}%`,
  delay: `${((i * 13) % 100) / 100}s`,
  duration: `${0.7 + ((i * 7) % 5) / 10}s`,
}));

/** A soft falling-rain overlay for the umbrella scene. */
export const RainLayer = ({ active }) => (
  <div className={`ch-rain ${active ? "is-active" : ""}`} aria-hidden="true">
    {RAINDROPS.map((d) => (
      <span
        key={d.id}
        className="ch-raindrop"
        style={{ left: d.left, animationDelay: d.delay, animationDuration: d.duration }}
      />
    ))}
  </div>
);

/**
 * A character that crossfades from its base pose to a happy pose when `happy`
 * flips true. Grounds itself with a soft contact shadow.
 */
export const CharacterView = ({ character, happy, className = "", style }) => {
  const showHappy = happy && character.happyAsset;
  return (
    <div className={`ch-character ${className}`} style={style} aria-hidden="true">
      <div className="ch-ground-shadow" />
      <div className="ch-character-stack">
        <Sprite name={character.asset} className={`ch-pose ${showHappy ? "is-hidden" : ""}`} />
        {character.happyAsset && (
          <Sprite name={character.happyAsset} className={`ch-pose ch-pose-happy ${showHappy ? "is-shown" : ""}`} />
        )}
      </div>
    </div>
  );
};

/** Reads a line aloud on tap — every instruction can be heard by pre-readers. */
export const SpeakerButton = ({ onSpeak, label = "Read aloud" }) => (
  <button type="button" className="ch-speaker" onClick={onSpeak} aria-label={label}>
    <SpeakerIcon size={18} />
  </button>
);

/**
 * The activity's one-line instruction on a frosted glass bar, always paired
 * with a speaker chip. Speaks the text on mount (and whenever it changes).
 */
export const PromptCapsule = ({ text, sounds }) => {
  React.useEffect(() => {
    if (text) sounds.speak(text);
    // Re-narrate only when the prompt text itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return (
    <div className="ch-prompt-capsule">
      <span>{text}</span>
      <button
        type="button"
        className="ch-speaker"
        onClick={() => sounds.speak(text)}
        aria-label="Read aloud"
      >
        <SpeakerIcon size={18} />
      </button>
    </div>
  );
};

/** A gold glow ring drop-target hint (used by carry / honest-return). */
export const GlowRing = ({ hot, tint = "#ffd45c", style }) => (
  <span
    className={`ch-glow-ring ${hot ? "is-hot" : ""}`}
    style={{ "--ch-ring-tint": tint, ...style }}
    aria-hidden="true"
  />
);

/**
 * Chunky golden star pips — one per round done. Filled stars pop in with a
 * spin; the empty ones sit as soft silhouettes so kids can count what's left.
 */
export const StarPips = ({ total, done }) => (
  <div className="ch-star-pips" aria-hidden="true">
    {Array.from({ length: total }, (_, i) => {
      const filled = i < done;
      return (
        <motion.span
          key={i}
          className={`ch-star-pip ${filled ? "is-filled" : ""}`}
          animate={filled ? { scale: [0.5, 1.35, 1], rotate: [-30, 14, 0] } : { scale: 1, rotate: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <StarShape size={24} color={filled ? "#f5aa21" : "rgba(42, 51, 85, 0.16)"} />
        </motion.span>
      );
    })}
  </div>
);

/**
 * The shared progress meter: a fat candy track with a travelling shine and a
 * star cap that lights up (and spins) the moment the meter fills.
 */
export const JuicyMeter = ({ progress, icon, tint = "#f5aa21", full = false }) => {
  const pct = Math.min(100, Math.max(0, Math.round(progress * 100)));
  const isFull = full || pct >= 100;
  return (
    <div className={`ch-juicy-meter ${isFull ? "is-full" : ""}`}>
      {icon && <span className="ch-juicy-icon" aria-hidden="true">{icon}</span>}
      <div className="ch-juicy-track">
        <motion.div
          className="ch-juicy-fill"
          style={{ "--ch-meter-tint": tint }}
          animate={{ width: `${pct}%` }}
          transition={{ type: "spring", stiffness: 170, damping: 24 }}
        >
          <span className="ch-juicy-shine" />
        </motion.div>
      </div>
      <motion.span
        className="ch-juicy-star"
        aria-hidden="true"
        animate={isFull ? { scale: [1, 1.5, 1.15], rotate: [0, 20, 0] } : { scale: 1, rotate: 0 }}
        transition={{ duration: 0.6 }}
      >
        <StarShape size={26} color={isFull ? "#f5aa21" : "rgba(42, 51, 85, 0.2)"} />
      </motion.span>
    </div>
  );
};

/**
 * The activity's status line on a frosted chip (replaces bare floating text).
 * Tones: "active" (coral), "oops" (coral + shake), "done" (green).
 */
export const CaptionChip = ({ tone = "", shakeKey, children }) => (
  <motion.p
    key={shakeKey}
    className={`ch-caption-chip ${tone ? `is-${tone}` : ""}`}
    animate={tone === "oops" ? { x: [0, -6, 6, -4, 4, 0] } : { x: 0 }}
    transition={{ duration: 0.4 }}
  >
    {children}
  </motion.p>
);

const CONFETTI_TINTS = ["#ff8f6b", "#ffd43b", "#5fce6a", "#4dabf7", "#b197fc", "#ff7a8a"];

/** A celebratory ribbon rain over the whole stage. Re-fires with `trigger`. */
export const ConfettiRain = ({ trigger = 0, count = 26 }) =>
  trigger > 0 ? (
    <div className="ch-confetti-rain" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <i
          key={`${trigger}-${i}`}
          style={{
            left: `${((i * 37) % 96) + 2}%`,
            animationDelay: `${(i % 8) * 0.12}s`,
            animationDuration: `${1.6 + ((i * 13) % 8) / 10}s`,
            background: CONFETTI_TINTS[i % CONFETTI_TINTS.length],
          }}
        />
      ))}
    </div>
  ) : null;

const AMBIENT_SPECKS = Array.from({ length: 10 }, (_, i) => ({
  id: i,
  left: `${((i * 41 + 13) % 94) + 3}%`,
  top: `${((i * 29 + 17) % 68) + 8}%`,
  delay: `${((i * 7) % 11) / 2.4}s`,
  duration: `${4 + ((i * 11) % 5)}s`,
  size: 4 + ((i * 3) % 5),
}));

/**
 * Soft drifting light-motes that make a stage feel alive without stealing
 * attention. "day" glows warm gold, "night" glows pale moonlight.
 */
export const AmbientLayer = ({ variant = "day" }) => (
  <div className={`ch-ambient is-${variant}`} aria-hidden="true">
    {AMBIENT_SPECKS.map((s) => (
      <span
        key={s.id}
        className="ch-ambient-speck"
        style={{
          left: s.left,
          top: s.top,
          width: s.size,
          height: s.size,
          animationDelay: s.delay,
          animationDuration: s.duration,
        }}
      />
    ))}
  </div>
);
